import React, { useEffect } from "react";
import { ArrowRight, Building2, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { HOSPITAL_PARTNERSHIP } from "@/lib/site-data";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function ForHospitalsPage() {
 useEffect(() => {
 window.scrollTo(0, 0);
 }, []);

 useDocumentMeta({
 title: "Hospitals, clinics, labs & pharmacies - Partner with TeleMedCare",
 description:
 "Pharmacies, labs and hospitals: receive referrals from Nigerian doctors, dispense prescriptions and surface results - all from one partner dashboard. Apply in minutes.",
 canonical: "https://telemedcare.app/for-hospitals",
 });

 const { hero, valueProps, requirements, partnershipCta } = HOSPITAL_PARTNERSHIP;

 return (
 <div className="min-h-screen bg-cream text-ink">
 <Navbar />
 <main className="pt-[72px]">
 <section className="relative overflow-hidden bg-forest text-cream">
 <div
 aria-hidden="true"
 className="absolute -bottom-24 -left-24 h-[380px] w-[380px] rounded-full bg-cream/10 blur-3xl"
 />
 <div className="container-x relative py-16 lg:py-24">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
 <div className="lg:col-span-6 order-2 lg:order-1">
 <div className="relative">
 <div
 aria-hidden="true"
 className="absolute -inset-3 -z-10 rounded-[32px] bg-cream/10"
 style={{ transform: "rotate(-1.25deg)" }}
 />
 <img
 src={hero.image}
 alt={hero.image_alt}
 className="w-full rounded-3xl object-contain bg-cream aspect-[3/2] shadow-2xl"
 />
 </div>
 </div>
 <div className="lg:col-span-6 order-1 lg:order-2">
 <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
 {hero.eyebrow}
 </p>
 <h1 className="mt-4 font-display text-[40px] sm:text-[52px] lg:text-[60px] font-extrabold leading-[1.05] tracking-tight text-balance">
 {hero.title}
 </h1>
 <p className="mt-6 text-[17px] text-cream/88 max-w-xl leading-relaxed">
 {hero.body}
 </p>
 <div className="mt-10">
 <a
 href={partnershipCta.href}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="hospital-book-call"
 className="inline-flex items-center gap-2 rounded-full bg-cream text-forest hover:bg-cream/95 px-7 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
 >
 <Building2 className="h-5 w-5" />
 {partnershipCta.label}
 <ArrowRight className="h-4 w-4" />
 </a>
 <p className="mt-4 text-[13.5px] text-cream/65 max-w-md">
 {partnershipCta.helper}
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="py-20 lg:py-24 border-b border-ink/10">
 <div className="container-x">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
 {valueProps.map((v) => (
 <div
 key={v.title}
 className="rounded-2xl border border-ink/10 bg-white p-8 shadow-sm"
 >
 <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-forest/10 text-forest">
 <Building2 className="h-5 w-5" />
 </span>
 <h2 className="mt-5 font-display text-[22px] font-bold tracking-tight">
 {v.title}
 </h2>
 <p className="mt-3 text-[15.5px] text-ink/70 leading-relaxed">
 {v.body}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 <section className="py-20 lg:py-28">
 <div className="container-x">
 <div className="max-w-3xl">
 <h2 className="font-display text-[32px] sm:text-[40px] font-extrabold tracking-tight text-balance">
 {requirements.title}
 </h2>
 <p className="mt-4 text-[17px] text-ink/70 leading-relaxed">
 {requirements.subtitle}
 </p>
 </div>
 <ul className="mt-14 space-y-5 max-w-4xl">
 {requirements.items.map((item) => (
 <li
 key={item.title}
 className="flex gap-4 rounded-2xl border border-ink/10 bg-cream/50 px-5 py-5 lg:px-7 lg:py-6"
 >
 <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest text-cream">
 <CheckCircle2 className="h-5 w-5" />
 </span>
 <div>
 <h3 className="font-display text-[18px] font-bold text-ink">
 {item.title}
 </h3>
 <p className="mt-2 text-[15px] text-ink/70 leading-relaxed">
 {item.detail}
 </p>
 </div>
 </li>
 ))}
 </ul>

 <div className="mt-16 rounded-3xl bg-ink text-cream px-8 py-10 lg:px-12 lg:py-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
 <div className="max-w-xl">
 <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
 Next step
 </p>
 <p className="mt-3 font-display text-[24px] sm:text-[28px] font-bold leading-snug">
 Ready to bring your facility onto TeleMedCare?
 </p>
 <p className="mt-2 text-[15px] text-cream/75">
 Submit your details and our partnerships team will reach out within 1-2 business days.
 </p>
 </div>
 <a
 href={partnershipCta.href}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="hospital-book-call-bottom"
 className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-8 py-4 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
 >
 {partnershipCta.label}
 <ArrowRight className="h-4 w-4" />
 </a>
 </div>
 </div>
 </section>
 </main>
 <Footer />
 </div>
 );
}
